import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ForgeTheme, forgeThemeLabel, nextForgeTheme } from '../../hooks/useForgeTheme';
import {
  FORGE_CLEANING_TASK_LIST_PATH,
  FORGE_DESIGNS_DASHBOARD_PATH,
  FORGE_DESIGN_GROUPS_DASHBOARD_PATH,
  FORGE_LOCKED_DESIGNS_DASHBOARD_PATH,
  FORGE_MASTER_LABEL_PATH,
  FORGE_PR_INFERENCE_OKRS_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_RECOGNITION_THRESHOLDS_CONFIG_PATH,
  FORGE_REMOTE_DC_CONFIG_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_REPORTED_ISSUES_PATH,
  FORGE_REVIEW_ISSUES_DASHBOARD_PATH,
  FORGE_STORE_DC_TASK_LIST_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
  FORGE_UNRESOLVED_TASK_LIST_PATH,
  FORGE_VIZSIM_DATASETS_DASHBOARD_PATH,
} from '../../paths';
import { MasterLabelLink } from '../common/MasterLabelLink';
import { ForgeAppSwitcher, ForgeEnvironmentSwitcher } from './ForgeEnvironmentSwitcher';
import { ForgeThemeIcon } from './ForgeThemeIcon';

interface ForgeSidebarProps {
  theme: ForgeTheme;
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  isMobile?: boolean;
  toggleCollapse: () => void;
  hideSidebar: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  hasRecognitionAdminAccess: boolean;
  hasRemoteDataCollectionAccess: boolean;
  recognitionOnly?: boolean;
  onMobileNavigate?: () => void;
}

interface NavSection {
  title: string;
  items: { label: string; icon: React.ReactNode; path: string }[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Dashboards',
    items: [
      {
        label: 'System Health',
        icon: <MonitorHeartOutlinedIcon />,
        path: FORGE_SYSTEM_HEALTH_PATH,
      },
      {
        label: 'PR Inference OKRs',
        icon: <QueryStatsOutlinedIcon />,
        path: FORGE_PR_INFERENCE_OKRS_PATH,
      },
      {
        label: 'Recognition OKRs',
        icon: <FactCheckOutlinedIcon />,
        path: FORGE_RECOGNITION_OKRS_PATH,
      },
      {
        label: 'Designs',
        icon: <StyleOutlinedIcon />,
        path: FORGE_DESIGNS_DASHBOARD_PATH,
      },
      {
        label: 'Design Groups',
        icon: <AccountTreeOutlinedIcon />,
        path: FORGE_DESIGN_GROUPS_DASHBOARD_PATH,
      },
      {
        label: 'Locked Designs',
        icon: <LockOutlinedIcon />,
        path: FORGE_LOCKED_DESIGNS_DASHBOARD_PATH,
      },
      {
        label: 'VizSim Datasets',
        icon: <DatasetOutlinedIcon />,
        path: FORGE_VIZSIM_DATASETS_DASHBOARD_PATH,
      },
      {
        label: 'Reported Issues',
        icon: <WarningAmberOutlinedIcon />,
        path: FORGE_REPORTED_ISSUES_PATH,
      },
    ],
  },
  {
    title: 'Task Lists',
    items: [
      {
        label: 'Store DC',
        icon: <ImageSearchOutlinedIcon />,
        path: FORGE_STORE_DC_TASK_LIST_PATH,
      },
      {
        label: 'Remote DC',
        icon: <TravelExploreOutlinedIcon />,
        path: FORGE_REMOTE_DC_TASK_LIST_PATH,
      },
      {
        label: 'Review Issues',
        icon: <AssignmentLateOutlinedIcon />,
        path: FORGE_REVIEW_ISSUES_DASHBOARD_PATH,
      },
      {
        label: 'Unresolved',
        icon: <WarningAmberOutlinedIcon />,
        path: FORGE_UNRESOLVED_TASK_LIST_PATH,
      },
      {
        label: 'Cleaning',
        icon: <CleaningServicesOutlinedIcon />,
        path: FORGE_CLEANING_TASK_LIST_PATH,
      },
    ],
  },
  {
    title: 'Labeling Tools',
    items: [
      {
        label: 'Remote DC',
        icon: <TravelExploreOutlinedIcon />,
        path: FORGE_REMOTE_DC_TOOL_PATH,
      },
      {
        label: 'Master Label',
        icon: <ViewColumnOutlinedIcon />,
        path: FORGE_MASTER_LABEL_PATH,
      },
      {
        label: 'PR review',
        icon: <RuleOutlinedIcon />,
        path: FORGE_PR_REVIEW_PATH,
      },
    ],
  },
  {
    title: 'Configuration',
    items: [
      {
        label: 'Remote DC',
        icon: <TuneOutlinedIcon />,
        path: FORGE_REMOTE_DC_CONFIG_PATH,
      },
      {
        label: 'Recognition thresholds',
        icon: <TuneOutlinedIcon />,
        path: FORGE_RECOGNITION_THRESHOLDS_CONFIG_PATH,
      },
    ],
  },
];

const RECOGNITION_PATHS = new Set([
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_THRESHOLDS_CONFIG_PATH,
]);

const REMOTE_DC_LABELER_PATHS = new Set([
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
]);

export const ForgeSidebar: React.FC<ForgeSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  isMobile = false,
  toggleCollapse,
  hideSidebar,
  onDragStart,
  toggleTheme,
  currentPath,
  hasRecognitionAdminAccess,
  hasRemoteDataCollectionAccess,
  recognitionOnly = false,
  onMobileNavigate,
}) => {
  const { logout, canUseNonAdminPreview, enterNonAdminPreview } = useAuthContext();
  const navigate = useNavigate();
  const effectiveCollapsed = isCollapsed && !isMobile;
  const nextTheme = nextForgeTheme(theme);
  const nextThemeLabel = forgeThemeLabel(nextTheme);
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (recognitionOnly) {
        return (
          (hasRecognitionAdminAccess && RECOGNITION_PATHS.has(item.path)) ||
          (hasRemoteDataCollectionAccess && REMOTE_DC_LABELER_PATHS.has(item.path))
        );
      }
      return (
        (hasRecognitionAdminAccess || !RECOGNITION_PATHS.has(item.path)) &&
        (hasRemoteDataCollectionAccess || !REMOTE_DC_LABELER_PATHS.has(item.path))
      );
    }),
  })).filter((section) => section.items.length > 0);

  const isActive = (path: string) => {
    if (path === FORGE_MASTER_LABEL_PATH) return currentPath === path;
    return currentPath.startsWith(path);
  };

  const sidebarClasses = [
    'forge-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Forge navigation"
    >
      {/* Nav */}
      <nav className="sidebar-nav">
        {visibleSections.map((section) => (
          <div key={section.title} className="sidebar-section">
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => {
              const SidebarLink = item.path === FORGE_MASTER_LABEL_PATH ? MasterLabelLink : Link;
              return (
                <SidebarLink
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                  title={effectiveCollapsed ? item.label : undefined}
                  onClick={onMobileNavigate}
                >
                  {item.icon}
                  <span className="sidebar-item-label">{item.label}</span>
                </SidebarLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          {canUseNonAdminPreview ? (
            <button
              className="sidebar-footer-link"
              title={effectiveCollapsed ? 'Labeler preview' : undefined}
              onClick={() => {
                onMobileNavigate?.();
                enterNonAdminPreview();
              }}
            >
              <VisibilityOutlinedIcon />
              <span className="sidebar-collapse-label">Labeler preview</span>
            </button>
          ) : (
            <button
              className="sidebar-footer-link"
              title={effectiveCollapsed ? 'Admin' : undefined}
            >
              <AdminPanelSettingsOutlinedIcon />
              <span className="sidebar-collapse-label">Admin</span>
            </button>
          )}

          <ForgeEnvironmentSwitcher />
          <ForgeAppSwitcher />

          <button
            className="sidebar-footer-link"
            onClick={toggleTheme}
            title={effectiveCollapsed ? `Switch to ${nextTheme} mode` : undefined}
          >
            <ForgeThemeIcon theme={nextTheme} />
            <span className="sidebar-collapse-label">{nextThemeLabel}</span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              logout();
              navigate('/stores');
            }}
            title={effectiveCollapsed ? 'Sign out' : undefined}
          >
            <LogoutIcon />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>

        {!isMobile ? (
          <>
            <div className="sidebar-footer-divider" />

            <button className="sidebar-collapse-btn" onClick={hideSidebar} title="Hide sidebar">
              <ChevronLeftIcon />
              <span className="sidebar-collapse-label">Hide</span>
            </button>
          </>
        ) : null}
      </div>

      {!isMobile ? <div className="sidebar-drag-handle" onMouseDown={onDragStart} /> : null}
    </aside>
  );
};
